import { NgTemplateOutlet } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import { Component, InjectionToken, OnChanges, OnInit, SimpleChanges, inject, input } from '@angular/core';
import { HrefPipe } from '../../../../pipes/href.pipe';
import { IconComponent } from '../../../../components/icon/icon.component';
import { AnalystRatings } from '../../../../domain/analyst-rating.domain';
import { MarketType } from '../../../../domain/market.domain';
import type { RatingsConsensus } from '../../../../domain/analyst-rating.domain';

export { AnalystRatings } from '../../../../domain/analyst-rating.domain';
export type { RatingsConsensus } from '../../../../domain/analyst-rating.domain';

export const ANALYST_RATING_CONFIGURATION = new InjectionToken<AnalystRatingConfiguration>('analystRatingConfiguration');

export interface AnalystRatingConfiguration {
  newAnalystRatings: boolean;
  analystRatingLinkId?: string;
}

/**
 *
 * Graphically depicts the analyst consensus for financial security.
 * An example usage of the directive:
 *
 * <td-wb-analyst-rating [consensus]="quote.ratingConsensus">
 * </td-wb-analyst-rating>
 *
 *  @param {string} consensus The analyst consensus, expected to be an element in the
 *  set { 'SELL', 'UNDERPERFORM', 'HOLD', 'BUY', 'STRONG_BUY', 'STRONG_SELL'}.
 */

@Component({
  selector: 'td-wb-analyst-rating',
  templateUrl: 'analyst-rating.component.html',
  styleUrls: ['analyst-rating.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    HrefPipe,
    IconComponent
  ]
})

export class AnalystRatingComponent implements OnInit, OnChanges {
  private readonly analystRatingsConfiguration = inject<AnalystRatingConfiguration>(ANALYST_RATING_CONFIGURATION);

  private static readonly ANALYST_RATING_LINK: string = 'link_markets_research_analysts';

  readonly consensus = input<RatingsConsensus | AnalystRatings>(undefined);
  readonly symbol = input<string>(undefined);
  readonly market = input<MarketType>(undefined);

  overallConsensus: AnalystRatings;
  newAnalystRatings: boolean;
  linkId: string = AnalystRatingComponent.ANALYST_RATING_LINK;
  // TODO Deprecate
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly AnalystRatings: typeof AnalystRatings = AnalystRatings;

  ngOnInit(): void {
    this.newAnalystRatings = this.analystRatingsConfiguration?.newAnalystRatings;
    if (this.analystRatingsConfiguration.analystRatingLinkId !== undefined) {
      this.linkId = this.analystRatingsConfiguration.analystRatingLinkId;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check what type consensus is. The old type is AnalystRatings, a new object is RatingsConsensus.
    // Eventually it will always be RatingsConsensus
    if (changes.consensus !== undefined) {
      const consensus = changes.consensus.currentValue;
      this.overallConsensus = consensus !== undefined && typeof consensus === 'object' ?
        consensus.overallConsensus : consensus as AnalystRatings;
    }
  }

  isRatingsConsensus(value: RatingsConsensus | AnalystRatings): value is RatingsConsensus {
    return value !== undefined && this.overallConsensus !== undefined;
  }

  /**
   * Returns the consensus narrowed to RatingsConsensus when the runtime check passes,
   * otherwise undefined. Used by the template strict typechecker because @if cannot
   * narrow a signal-call expression.
   */
  asRatingsConsensus(): RatingsConsensus | undefined {
    const value = this.consensus();
    return this.isRatingsConsensus(value) ? value : undefined;
  }

  showNewAnalystRatings(): boolean {
    return this.newAnalystRatings;
  }

  showAnalystRatings(): boolean {
    return this.overallConsensus !== undefined;
  }

  isRating(rating: AnalystRatings): boolean {
    let adjustedRating: AnalystRatings;
    if (this.overallConsensus === AnalystRatings.SELL || this.overallConsensus === AnalystRatings.STRONG_SELL) {
      adjustedRating =  AnalystRatings.SELL;
    } else if (this.overallConsensus === AnalystRatings.BUY || this.overallConsensus === AnalystRatings.STRONG_BUY) {
      adjustedRating = AnalystRatings.BUY;
    } else {
      adjustedRating = this.overallConsensus;
    }
    return adjustedRating === rating;
  }

  getLinkParams(): string {
    return `/${this.market()}/${this.symbol()}`;
  }
}
